import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import Icon from './Icon.jsx';

// "Quick question from Howler" — the client-facing half of product surveys
// (server/productSurveys.js). Asks the server for the ONE survey this person
// should see right now in the client they're looking at and shows it the way
// the survey asks: a POPUP over the workspace with the questions already open
// (the default — a banner went unanswered, so this one can't be scrolled past),
// or a compact CARD under the status banner they open in place. Either way:
// answer, or push it away ("Not now" → a week; "Don't ask again" → gone).
//
// Mobile-first: a single stacked column, every control ≥ 40px, the question
// list scrolls with the page rather than inside a box. Best-effort: any
// failure hides the card — a survey must never get in the way of the work.
//
// Polls gently (on mount, on entity change, when the tab comes back after ~10
// minutes) — a product survey is a once-a-fortnight thing, not live data.

const POLL_MS = 10 * 60_000;
const isChoice = (t) => t === 'single_choice' || t === 'multiple_choice';

// `entityId` = the client being viewed (a client user always has one). `admin`
// = the Howler admin console, where there is no client: admins are asked the
// surveys that name Howler admins (or them personally).
// `preview` = a Howler admin looking at a client's workspace (Client preview):
// ask for what one of THAT client's users would see, not the admin's own card.
export default function ProductSurveyPrompt({ entityId = '', enabled = true, admin = false, preview = false }) {
  const [survey, setSurvey] = useState(null);
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({}); // questionId → rating | index | [indices] | text
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const poll = useCallback(async () => {
    if ((!entityId && !admin) || !enabled) { setSurvey(null); return; }
    try {
      const r = await api.myProductSurveyPending(entityId || '', preview && !!entityId);
      setSurvey(r.survey || null);
    } catch { setSurvey(null); }
  }, [entityId, enabled, admin, preview]);

  useEffect(() => {
    setOpen(false); setAnswers({}); setErr(''); setDone(false);
    poll();
    let last = Date.now();
    const t = setInterval(() => { last = Date.now(); poll(); }, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible' && Date.now() - last > POLL_MS) { last = Date.now(); poll(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [poll]);

  if (!survey || !enabled) return null;
  const popup = survey.display === 'popup';
  const showQuestions = open || popup;

  const set = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  const toggleMulti = (qid, i) => setAnswers((a) => {
    const cur = Array.isArray(a[qid]) ? a[qid] : [];
    return { ...a, [qid]: cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i] };
  });

  // Build the wire answers (the shared validator's shape). Unanswered optional
  // questions are simply left out; the server enforces required ones.
  const wire = () => survey.questions.map((q) => {
    const v = answers[q.id];
    if (q.type === 'rating') return v ? { questionId: q.id, type: q.type, rating: v } : null;
    if (q.type === 'single_choice') return Number.isInteger(v) ? { questionId: q.id, type: q.type, selectedIndex: v } : null;
    if (q.type === 'multiple_choice') return Array.isArray(v) && v.length ? { questionId: q.id, type: q.type, selectedIndices: v } : null;
    return v && String(v).trim() ? { questionId: q.id, type: q.type, text: String(v).trim() } : null;
  }).filter(Boolean);

  const missing = survey.questions.filter((q) => q.required && !wire().some((a) => a.questionId === q.id));

  const submit = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const standalone = typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone);
      await api.myProductSurveyRespond(survey.id, { entityId, answers: wire(), platform: standalone ? 'pulse-app' : 'pulse-web' });
      setDone(true);
      setTimeout(() => { setSurvey(null); setDone(false); }, 2500);
    } catch (e) { setErr(e.message || 'Could not send — try again'); }
    finally { setBusy(false); }
  };
  const dismiss = async (kind) => {
    setSurvey(null); // optimistic — the card is gone either way
    try { await api.myProductSurveyDismiss(survey.id, { entityId, kind }); } catch { /* best-effort */ }
  };

  const body = (
    <div style={{ ...wrap, ...(popup ? sheet : admin ? { borderRadius: 12, marginBottom: 16 } : {}) }} role={popup ? 'dialog' : undefined} aria-modal={popup ? 'true' : undefined} aria-label={popup ? 'Quick question from Howler' : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Icon name="surveys" size={18} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.8 }}>Quick question from Howler</div>
          <div style={{ fontSize: 13.5, fontWeight: 700 }}>{survey.prompt || survey.title}</div>
        </div>
        {!showQuestions && !done && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" style={primary} onClick={() => setOpen(true)}>Answer{survey.questions.length > 1 ? ` · ${survey.questions.length} questions` : ''}</button>
            <button type="button" style={ghost} onClick={() => dismiss('snooze')}>Not now</button>
            <button type="button" style={{ ...ghost, opacity: 0.8 }} onClick={() => dismiss('never')} title="Hide this survey for good">Don’t ask again</button>
          </div>
        )}
      </div>

      {done && <p style={{ margin: '10px 0 0', fontSize: 13.5, fontWeight: 600 }}>Thank you — that goes straight to the team.</p>}

      {showQuestions && !done && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {survey.description && <p style={{ margin: 0, fontSize: 13, opacity: 0.9, lineHeight: 1.45 }}>{survey.description}</p>}
          {survey.questions.map((q, i) => (
            <div key={q.id}>
              <div style={{ fontSize: 13.5, fontWeight: 650, marginBottom: 7 }}>{i + 1}. {q.text}{q.required && <span title="Required"> *</span>}</div>
              {q.type === 'rating' && (
                <div style={{ display: 'flex', gap: 4 }} role="radiogroup" aria-label={q.text}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" role="radio" aria-checked={answers[q.id] === n} aria-label={`${n} of 5`} onClick={() => set(q.id, n)}
                      style={{ ...star, color: (answers[q.id] || 0) >= n ? '#ffb340' : 'rgba(255,255,255,0.45)' }}>★</button>
                  ))}
                </div>
              )}
              {isChoice(q.type) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(q.options || []).map((o, oi) => {
                    const on = q.type === 'single_choice' ? answers[q.id] === oi : (answers[q.id] || []).includes(oi);
                    return (
                      <button key={oi} type="button" role={q.type === 'single_choice' ? 'radio' : 'checkbox'} aria-checked={on}
                        onClick={() => (q.type === 'single_choice' ? set(q.id, oi) : toggleMulti(q.id, oi))}
                        style={{ ...option, background: on ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.10)', borderColor: on ? '#fff' : 'rgba(255,255,255,0.35)' }}>
                        <span style={{ width: 14, height: 14, flex: 'none', borderRadius: q.type === 'single_choice' ? '50%' : 4, border: '2px solid #fff', background: on ? '#fff' : 'transparent' }} />
                        <span>{o}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {q.type === 'text' && (
                <textarea value={answers[q.id] || ''} onChange={(e) => set(q.id, e.target.value)} rows={2} maxLength={1000} placeholder="Type here…" style={textarea} />
              )}
            </div>
          ))}
          {err && <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>⚠ {err}</p>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" style={primary} disabled={busy || missing.length > 0} onClick={submit} title={missing.length ? `Please answer: ${missing.map((q) => q.text).join(' · ')}` : undefined}>{busy ? 'Sending…' : 'Send'}</button>
            <button type="button" style={ghost} onClick={() => dismiss('snooze')}>Not now</button>
            {popup && <button type="button" style={{ ...ghost, opacity: 0.8 }} onClick={() => dismiss('never')} title="Hide this survey for good">Don’t ask again</button>}
            {missing.length > 0 && <span style={{ fontSize: 12, opacity: 0.85 }}>{missing.length === 1 ? '1 required question left' : `${missing.length} required questions left`}</span>}
          </div>
        </div>
      )}
    </div>
  );
  // The popup sits over everything, centred on a desktop and as a bottom sheet
  // on a phone; the backdrop deliberately does nothing — the buttons are the
  // only way out, which is the point of a popup.
  if (!popup) return body;
  return <div style={overlay}>{body}</div>;
}

const wrap = { background: 'linear-gradient(90deg, var(--brand, #6d5df6), var(--brand-2, #ff6b35))', color: '#fff', padding: '12px 16px' };
const overlay = { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(16,16,19,0.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 'max(12px, env(safe-area-inset-top)) 0 0' };
const sheet = { width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box', padding: '18px 20px calc(18px + env(safe-area-inset-bottom))', borderRadius: '18px 18px 0 0', boxShadow: '0 -12px 40px rgba(0,0,0,0.35)', margin: '0 auto' };
const primary = { minHeight: 40, padding: '8px 14px', borderRadius: 980, border: 'none', background: '#fff', color: 'var(--brand, #6d5df6)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const ghost = { minHeight: 40, padding: '8px 12px', borderRadius: 980, border: '1.5px solid rgba(255,255,255,0.55)', background: 'transparent', color: '#fff', fontSize: 12.5, fontWeight: 650, cursor: 'pointer' };
const star = { minWidth: 40, minHeight: 40, fontSize: 26, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer', padding: 0 };
const option = { display: 'flex', alignItems: 'center', gap: 9, minHeight: 40, padding: '8px 12px', borderRadius: 10, border: '1.5px solid', color: '#fff', fontSize: 13, textAlign: 'left', cursor: 'pointer', width: '100%', boxSizing: 'border-box' };
const textarea = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.55)', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical', minHeight: 44 };
